/**
 * LEADS — and the rule that a week of silence is an answer.
 *
 * Anthony: "they are lost when they are more than 7 days old without
 * getting a quote." That is a real policy, not a display preference, so it
 * lives here next to the queries rather than being re-decided by whichever
 * screen is asking.
 *
 * The rule is applied at READ time and can also be WRITTEN. Both, on
 * purpose: reading it means the waiting pile is honest the moment you open
 * it, without a nightly job having run; writing it means the record
 * eventually says what everybody already knows, and a lead that aged out
 * three months ago is not still technically "new" in the database.
 */

import { db } from "./client.ts";
import { isOpeningCall, stageOf, type LeadStage } from "../domain/leadstage.ts";
import { IMPORT_WATERMARK } from "../domain/watermark.ts";
import { campaignOf, lineEventId, type Campaign } from "../domain/leadcampaign.ts";
import { carSaveRefusal } from "../domain/carsave.ts";

export const AGED_DAYS = 7;
const DAY = 86400000;
export const agedBefore = (): string => new Date(Date.now() - AGED_DAYS * DAY).toISOString();

export type Lead = {
  id: string;
  status: "new" | "contacted" | "booked" | "converted" | "lost" | "cancelled";
  customer_name: string | null;
  customer_phone: string | null;
  customer_email: string | null;
  service_wanted: string | null;
  location: string | null;
  source: string | null;
  notes: string | null;
  preferred_at: string | null;
  created_at: string;
  customer_id: string | null;
  vehicle_id: string | null;
  converted_job_id: string | null;
  /**
   * The job the quote was raised on, when there is one. Read so the piles
   * can tell a quote that is OUT from a quote that was said yes to.
   */
  jobs: {
    status: string;
    quote_expires_at: string | null;
    changes_requested_at: string | null;
    job_authorizations: Array<{ id: string }> | null;
  } | null;
  /**
   * Attached on read for the waiting pile: when this thread last moved,
   * whoever moved it. Not a column — it comes from the conversation.
   */
  moved_at?: string;
  /** Two texts out, nothing back, ten minutes gone. Ring them. */
  call_now?: boolean;
  /**
   * WHO SPOKE LAST, so the card can say the true thing rather than a
   * true-sounding one. "Since anyone spoke" was counting from OUR
   * automatic text, and a robot texting somebody is not anyone speaking.
   */
  last_move?: { at: string; who: "them" | "us"; kind?: string | null } | null;
  /**
   * WHERE THIS LEAD IS IN THE TEXTBACK CAMPAIGN, read from the
   * conversation rather than from `status`. See domain/leadstage.ts: the
   * card used to say "not contacted" beside a red tag saying we had texted
   * twice, because `status` only moves when OUR task both sends and
   * updates the row.
   */
  stage?: LeadStage;
  /** The last thing said, and by whom. Shown on the tile. */
  last_said?: { body: string; who: "them" | "us" } | null;
  /**
   * WHAT THE FRONT DESK FEED SAYS ABOUT THIS PERSON, attached on read.
   *
   * Not computed a second time here. The feed decides red and orange in
   * one place on the server, and a screen that worked it out again from
   * the same facts would eventually disagree with the bell in the corner
   * — and then nobody would trust either.
   */
  flag?: "red" | "orange" | null;
  flag_why?: string | null;
  /**
   * HOW MANY ROWS THIS CARD STANDS FOR. Set by oneCardPerPerson when the
   * same phone number arrived more than once. Absent means one row, which
   * is the normal case and needs no badge.
   */
  dupes?: number;
  /** The other rows folded into this card, so nothing is silently hidden. */
  also?: string[];
  /**
   * WHICH KIND OF RED. Anthony: "do mark a lead that goes red to call
   * instantly because its a landline, landline so that we notice that's a
   * priority callback."
   *
   * Two things turn a lead red and they are not the same job. A stranger
   * who ignored two texts might still answer the third thing that happens.
   * A LANDLINE has had nothing at all and cannot -- the shop's entire
   * automatic response to that person was a message that could never
   * arrive. That one is a phone call now, not a phone call eventually.
   */
  flag_kind?: string | null;
  /** Where it came from as a place ("Hem-GMB", "Sup-FF"). Attached on read. */
  campaign?: Campaign | null;
  external_ref?: string | null;
};

const SEL = "id,status,customer_name,customer_phone,customer_email,service_wanted,location," +
  "source,external_ref,notes,preferred_at,created_at,customer_id,vehicle_id,converted_job_id," +
  // The quote raised off this lead, if any. See pileOf.
  "jobs:converted_job_id(status,quote_expires_at,changes_requested_at," +
  "job_authorizations(id))";

/** A lead nobody answered inside the week. Still `new` in the database. */
/**
 * Nobody has rung this person. `status` is the honest signal: a lead moves
 * to 'contacted' when somebody reaches out, so one still sitting at 'new'
 * has had nothing.
 */
export const neverContacted = (l: Lead): boolean => l.status === "new";

/** New, untouched, and old enough that it is now a problem. */
export const goneQuiet = (l: Lead): boolean =>
  neverContacted(l) && Date.now() - new Date(l.created_at).getTime() > 2 * DAY;

export const hasAgedOut = (l: Lead): boolean =>
  (l.status === "new" || l.status === "contacted") &&
  !l.converted_job_id &&
  Date.parse(l.created_at) < Date.now() - AGED_DAYS * DAY;

export type Pile = "waiting" | "booked" | "lost";

/** Whether the quote raised off this lead has been said yes to. */
export const quoteApproved = (l: Lead): boolean =>
  Boolean(l.jobs) && (
    (l.jobs?.job_authorizations ?? []).length > 0
    || ["scheduled", "in_progress", "completed", "invoiced", "paid"]
         .includes(l.jobs?.status ?? ""));

/**
 * WHICH PILE A LEAD IS REALLY IN.
 *
 * Anthony: "Reset if they are waiting on quotes, leave approved quotes
 * showing here."
 *
 * A lead moved to Booked the moment a quote was RAISED off it, because
 * `converted_job_id` was read as "this became work". It does not mean
 * that. It means somebody priced it. So a customer sitting on a quote they
 * have not answered vanished out of Waiting into a pile called Booked, and
 * the one screen an advisor works in the morning stopped showing the very
 * people who most need chasing. The quote drafter made this worse by the
 * hour, because it raises a job on every lead it can price.
 *
 * A quote that is OUT is still waiting. A quote that was APPROVED is
 * booked, and stays on this screen rather than disappearing — that is what
 * Booked is for.
 */
export function pileOf(l: Lead): Pile {
  if (l.status === "lost" || l.status === "cancelled") return "lost";

  /**
   * A CANCELLED JOB DOES NOT LEAVE A BOOKED LEAD.
   *
   * `status` goes to "converted" the moment a job is raised, and it never
   * goes back. So when the job was then cancelled, this line called the
   * lead Booked for ever: Erik Cueva filled the form in at 11:26, held a
   * good conversation until 14:17, had a quote raised at 14:21 and
   * cancelled at 14:22, and sat in Booked as won work while he waited for
   * a price nobody was going to send.
   *
   * The reasoning below already knows what a cancelled quote means. This
   * shortcut was jumping over it.
   */
  if ((l.status === "booked" || l.status === "converted")
      && l.jobs?.status !== "cancelled") return "booked";
  if (l.converted_job_id) {
    if (quoteApproved(l)) return "booked";
    // Priced, not answered. Cancelled is the one quote that is not still
    // waiting on anybody.
    return l.jobs?.status === "cancelled" ? "lost" : "waiting";
  }
  return hasAgedOut(l) ? "lost" : "waiting";
}

/**
 * `since` is the railway's window traveling with the click. A tile that
 * says nine and opens a list of four hundred is a tile nobody believes
 * again, so the same filter has to survive the journey.
 */
/**
 * WHEN THIS THREAD LAST MOVED — whoever moved it.
 *
 * Anthony: "leads should be sorted by the latest contacted customer that
 * is not a quote. whether they contacted us or the textback automation
 * did. Keep advisors and owners current on front desk actions."
 *
 * So the order is LAST ACTIVITY, not when the lead arrived. A lead from
 * Tuesday that somebody texted twenty minutes ago is live work; one that
 * came in an hour ago and has sat untouched is not the thing to look at
 * first. Both directions count, and automation counts as contact — the
 * question a desk is asking is "what moved recently", not "who spoke".
 *
 * Falls back to the lead's own created_at where there is no conversation,
 * so a brand new lead with no messages yet still lands at the top where
 * it belongs.
 */
/**
 * WHAT THIS NUMBER'S THREAD LOOKS LIKE — when it last moved, and whether
 * it has gone quiet after we tried twice.
 */
export type Thread = {
  at: string;
  /** Outbound messages since the lead came in. */
  tried: number;
  /** True when they have said something since our last message. */
  answered: boolean;
  /** When we last spoke to them. */
  lastOut: string | null;
  /**
   * Somebody actually RANG them, rather than sending another text. This is
   * what the red tag is asking for, so it is what takes the red tag off.
   */
  called: boolean;
  /** When THEY last said something. Null if they never have. */
  lastIn: string | null;
  /** What that last thing of theirs was: a call, a text, an email. */
  lastInKind: string | null;
  /** The newest message either way, so the tile can show it. */
  lastBody: string | null;
  lastWho: "them" | "us" | null;
};

async function lastActivity(leads: Lead[]): Promise<Map<string, Thread>> {
  const out = new Map<string, Thread>();
  const phones = [...new Set(leads
    .map((l) => String(l.customer_phone ?? "").replace(/\D/g, "").slice(-10))
    .filter((p) => p.length === 10))];
  if (!phones.length) return out;

  // When each number's card was born, so the call that created it is not
  // read back as them answering. The newest row is the card (see
  // oneCardPerPerson), so the latest stamp per number is the one to use.
  const born = new Map<string, string>();
  for (const l of leads) {
    const p = String(l.customer_phone ?? "").replace(/\D/g, "").slice(-10);
    if (p.length !== 10) continue;
    const was = born.get(p);
    if (!was || l.created_at > was) born.set(p, l.created_at);
  }

  const sb = await db();
  // Newest first and capped: the top of this list is all that can change
  // an order, and reading every message a shop has ever sent to answer a
  // sort would cost more than the sort is worth.
  /**
   * ONLY REAL TRAFFIC. Anthony: "time function as the leads move and they
   * contact etc seems to be an issue."
   *
   * It was counting every row in the ledger, and half of them are not
   * somebody speaking: a CallRail note saying which advert a call came
   * from, a "DnD enabled by customer" system row, a bridge marker. Each of
   * those moved a lead's clock, so a card said "2 min since anyone spoke"
   * when nobody had said anything at all and nothing had changed.
   */
  const { data } = await sb.from("comm_events")
    .select("phone10,occurred_at,direction,kind,body")
    .in("phone10", phones)
    .in("kind", ["sms", "call", "email"])
    .order("occurred_at", { ascending: false })
    .limit(4000);

  const rows = (data ?? []) as Array<{
    phone10: string; occurred_at: string; direction: string; kind: string | null;
    body: string | null }>;
  for (const r of rows) {
    const rang = r.direction === "outbound" && r.kind === "call";
    const said = (r.body ?? "").trim();
    // The ring that opened the lead is how they arrived, not a reply.
    const theirs = r.direction === "inbound" && !isOpeningCall(r, born.get(r.phone10));
    const t = out.get(r.phone10);
    if (!t) {
      // The first row for a number is the NEWEST, so whether they answered
      // is simply whether that newest message came from them.
      out.set(r.phone10, {
        at: r.occurred_at,
        tried: r.direction === "outbound" ? 1 : 0,
        answered: theirs,
        lastOut: r.direction === "outbound" ? r.occurred_at : null,
        lastIn: theirs ? r.occurred_at : null,
        lastInKind: theirs ? r.kind : null,
        called: rang,
        // Rows come back newest first, so the first one carrying words is
        // the last thing anybody said.
        lastBody: said || null,
        lastWho: said ? (r.direction === "inbound" ? "them" : "us") : null,
      });
      continue;
    }
    if (r.direction === "outbound") {
      t.tried += 1;
      if (!t.lastOut) t.lastOut = r.occurred_at;
    } else if (theirs && !t.lastIn) {
      t.lastIn = r.occurred_at;
      t.lastInKind = r.kind;
    }
    if (rang) t.called = true;
    if (!t.lastBody && said) {
      t.lastBody = said;
      t.lastWho = r.direction === "inbound" ? "them" : "us";
    }
  }
  return out;
}

/**
 * TWO TEXTS AND NOTHING BACK — STOP TYPING AND RING THEM.
 *
 * Anthony: "if a potential customer called and doesn't respond to either
 * textback, ten minutes later turn the lead read and say to call it
 * basically."
 *
 * The opener goes out, the nudge goes five minutes later, and if there is
 * still nothing ten minutes after that, the thread is not going to answer
 * itself. A third text is a shop talking to itself; a phone call is a
 * shop with a customer.
 *
 * It is read off the CONVERSATION rather than off our automation log,
 * because right now the old deployment sends those texts and this system
 * only sees them through the bridge. What matters is that two things went
 * out and nothing came back, whoever sent them.
 */
const CALL_AFTER_MS = 10 * 60_000;

export function needsCalling(l: Lead, seen: Map<string, Thread>): boolean {
  if (l.status !== "new" && l.status !== "contacted") return false;

  /**
   * IT COMES OFF ONCE SOMEBODY HAS RUNG THEM. Anthony: "Remove red tag
   * after customer is contacted from leads."
   *
   * The tag says one thing — pick up the phone — and it was staying red
   * until the CUSTOMER replied, which is not what it asks for. Somebody
   * who rang and got voicemail had done the job and the screen still shouted
   * at them, so the tag stopped meaning anything and got ignored, which is
   * worse than not having it.
   *
   * A call, not another text. Two texts and nothing back is how the tag
   * turns red in the first place; a third does not answer it.
   */
  const p10 = String(l.customer_phone ?? "").replace(/\D/g, "").slice(-10);
  if (p10.length !== 10) return false;

  /**
   * AND IT IS OFF WHILE THEY ARE HOLDING A PRICE. "Reset if they are
   * waiting on quotes."
   *
   * A lead with a quote out is not a lead nobody has spoken to. The ball
   * is with the customer and the shop is waiting on an answer, which is a
   * different kind of quiet from the one this tag is for. If the quote
   * goes cold, the Quotes screen is where that shows.
   */
  if (l.converted_job_id) return false;

  const t = seen.get(p10);
  if (!t || t.answered || t.called) return false;
  if (t.tried < 2) return false;
  if (!t.lastOut) return false;
  return Date.now() - Date.parse(t.lastOut) >= CALL_AFTER_MS;
}

/**
 * WHAT THE FEED HAS OPEN AGAINST THESE NUMBERS.
 *
 * One read, keyed by phone, so the color on a lead card and the color on
 * the bell are the same fact rather than two calculations that agree today.
 */
async function openFlags(
  leads: Lead[],
): Promise<Map<string, { level: "red" | "orange"; title: string; kind: string }>> {
  const out = new Map<string, { level: "red" | "orange"; title: string; kind: string }>();
  const phones = [...new Set(leads
    .map((l) => String(l.customer_phone ?? "").replace(/\D/g, "").slice(-10))
    .filter((p) => p.length === 10))];
  if (!phones.length) return out;

  const sb = await db();
  const { data } = await sb.from("notifications")
    .select("level,kind,title,phone10")
    .is("cleared_at", null).in("phone10", phones)
    .order("created_at", { ascending: false }).limit(300);

  for (const n of (data ?? []) as Array<{
    level: "red" | "orange"; kind: string; title: string; phone10: string | null }>) {
    if (!n.phone10) continue;
    // Red wins: it is the one that means nobody has spoken to them. And a
    // LANDLINE beats the other red, because it is the one that can never
    // answer a text.
    const had = out.get(n.phone10);
    if (had) {
      if (had.kind === "landline_caller") continue;
      if (had.level === "red" && n.kind !== "landline_caller") continue;
      if (n.level !== "red" && had.level === "red") continue;
    }
    out.set(n.phone10, { level: n.level, title: n.title, kind: n.kind });
  }
  return out;
}

/**
 * WHO SPOKE LAST AND WHEN, or nothing if the thread is empty.
 *
 * Ours and theirs are different facts and the screen has to say which. A
 * lead that got the automatic opener four minutes ago and a lead whose
 * customer wrote back four minutes ago look identical on a clock and are
 * opposite situations.
 */
export function lastMove(
  l: Lead, seen: Map<string, Thread>,
): { at: string; who: "them" | "us"; kind?: string | null } | null {
  const p10 = String(l.customer_phone ?? "").replace(/\D/g, "").slice(-10);
  const t = p10.length === 10 ? seen.get(p10) : undefined;
  if (!t) return null;
  const inAt = t.lastIn ? Date.parse(t.lastIn) : 0;
  const outAt = t.lastOut ? Date.parse(t.lastOut) : 0;
  if (!inAt && !outAt) return null;
  // Nothing before the lead existed counts as this lead moving.
  const born = Date.parse(l.created_at);
  if (Math.max(inAt, outAt) < born) return null;
  return inAt >= outAt
    ? { at: t.lastIn as string, who: "them", kind: t.lastInKind }
    : { at: t.lastOut as string, who: "us" };
}

/** The moment this lead last moved, for ordering. */
export const movedAt = (l: Lead, seen: Map<string, Thread>): string => {
  const p10 = String(l.customer_phone ?? "").replace(/\D/g, "").slice(-10);
  const talk = p10.length === 10 ? seen.get(p10)?.at : undefined;
  return talk && talk > l.created_at ? talk : l.created_at;
};

/**
 * ONE CARD PER PERSON.
 *
 * Anthony: "the leads cards are doubling up a lot on the leads page look
 * at how roman is."
 *
 * Jonathan Roman rang four times in six minutes. CallRail gave each call
 * its own id, the poller dedupes on that id, and four ids meant four
 * leads -- then the legacy bridge added a fifth. Across a fortnight that
 * is 210 extra cards on 598 rows: better than a third of the screen is
 * the same people listed twice.
 *
 * A person is a phone number here, not a row. Nothing is deleted and
 * nothing is merged in the database -- this is the screen refusing to show
 * the same human twice, which is safe in a way that a merge is not.
 *
 * ─── WHICH ROW WINS, AND WHY IT IS NOT SIMPLY THE NEWEST ───────────────
 *
 * The newest row is the base, because the freshest contact is what the
 * desk needs to see. But the newest CallRail row says "Rang in" and knows
 * nothing, while an older sibling may carry the quote, the customer
 * record, what they actually wanted, or a real name instead of "Wireless
 * Caller". So the base is FILLED IN from its siblings, oldest last, and
 * every field only if the base is missing it. Collapsing must not lose a
 * quote link -- that is how a booked job becomes invisible.
 */
const ten10 = (v: string | null | undefined): string =>
  String(v ?? "").replace(/\D/g, "").slice(-10);

/** A name the phone system invented, which any real name should beat. */
const isPlaceholderName = (n: string | null | undefined): boolean =>
  !n || /^(caller\s*\d+|wireless caller|unknown|google|no caller id)/i.test(n.trim());

export function oneCardPerPerson(rows: Lead[]): Lead[] {
  const by = new Map<string, Lead[]>();
  const out: Lead[] = [];

  for (const l of rows) {
    const p10 = ten10(l.customer_phone);
    // No number is no way to tell two people apart, so those stay as they
    // are rather than being lumped into one card by accident.
    if (p10.length !== 10) { out.push(l); continue; }
    const g = by.get(p10);
    if (g) g.push(l); else by.set(p10, [l]);
  }

  for (const group of by.values()) {
    const kept = { ...group[0]! } as Lead;
    if (group.length > 1) {
      for (const other of group.slice(1)) {
        if (!kept.converted_job_id && other.converted_job_id) {
          kept.converted_job_id = other.converted_job_id;
          kept.jobs = other.jobs;
        }
        if (!kept.customer_id && other.customer_id) kept.customer_id = other.customer_id;
        if (!kept.customer_email && other.customer_email) kept.customer_email = other.customer_email;
        if (!kept.location && other.location) kept.location = other.location;
        if (!kept.vehicle_id && other.vehicle_id) kept.vehicle_id = other.vehicle_id;
        if (isPlaceholderName(kept.customer_name) && !isPlaceholderName(other.customer_name)) {
          kept.customer_name = other.customer_name;
        }
        // "Rang in" and "Missed call" are what the phone system knows, not
        // what the person said. Anything a human or the intake agent wrote
        // beats them.
        const bland = (w: string | null | undefined) =>
          !w || /^(rang in|missed call)$/i.test(w.trim());
        if (bland(kept.service_wanted) && !bland(other.service_wanted)) {
          kept.service_wanted = other.service_wanted;
        }
      }
      kept.dupes = group.length;
      kept.also = group.slice(1).map((o) => o.id);
    }
    out.push(kept);
  }
  return out;
}

/**
 * BOTH ENDS OF THE WINDOW, not just the near one.
 *
 * Anthony, 2026-09-09: "the leads dashboard on booked and lost should let
 * me define date range as well."
 *
 * Waiting is a queue and only ever wants "recently". Booked and Lost are
 * records, and the question asked of a record is "how did June go", which
 * a since-only filter cannot answer -- every window it can describe ends
 * today. So there is an `until` now, and the two piles that are records
 * get a control for it.
 *
 * The column is `created_at`, which is the day the lead arrived rather than
 * the day it was booked or written off. That is stated on the screen rather
 * than glossed over: there is no booked_at column to filter on, and
 * inventing one is a migration, not a filter.
 */
/**
 * THE CAMPAIGN TAG ON EVERY CARD. Anthony, 2026-10-20: "track what
 * campaign it came from instead of things like callrail, ghl etc".
 *
 * A call lead's line is on the call it came from; a text lead's is on the
 * person's texts. Read in chunks because 300 event ids in one URL is past
 * what PostgREST takes. A failed read leaves the tag on the pipe's
 * fallback ("Call") rather than breaking the board.
 */
async function attachCampaigns(rows: Lead[]): Promise<Lead[]> {
  const sb = await db();
  const line = new Map<string, string>();
  const ids = [...new Set(rows.map((l) => lineEventId(l.source, l.external_ref)).filter(Boolean))] as string[];
  const texters = [...new Set(rows.filter((l) => l.source === "callrail text")
    .map((l) => String(l.customer_phone ?? "").replace(/\D/g, "").slice(-10))
    .filter((p) => p.length === 10))];
  const byPhone = new Map<string, string>();

  const reads: Array<PromiseLike<unknown>> = [];
  for (let i = 0; i < ids.length; i += 80) {
    reads.push(sb.from("comm_events").select("external_id,tracking_number")
      .in("external_id", ids.slice(i, i + 80)).not("tracking_number", "is", null)
      .then(({ data }) => {
        for (const r of (data ?? []) as Array<{ external_id: string; tracking_number: string }>) {
          line.set(r.external_id, r.tracking_number);
        }
      }));
  }
  if (texters.length) {
    reads.push(sb.from("comm_events").select("phone10,tracking_number")
      .in("phone10", texters).eq("kind", "sms").eq("direction", "inbound")
      .not("tracking_number", "is", null).limit(1000)
      .then(({ data }) => {
        for (const r of (data ?? []) as Array<{ phone10: string; tracking_number: string }>) {
          if (!byPhone.has(r.phone10)) byPhone.set(r.phone10, r.tracking_number);
        }
      }));
  }
  await Promise.all(reads);

  for (const l of rows) {
    const ev = lineEventId(l.source, l.external_ref);
    const p10 = String(l.customer_phone ?? "").replace(/\D/g, "").slice(-10);
    l.campaign = campaignOf(l.source, ev ? line.get(ev) : byPhone.get(p10));
  }
  return rows;
}

export async function listLeads(
  pile: Pile, since?: string | null, until?: string | null,
): Promise<Lead[]> {
  return attachCampaigns(await listLeadsBare(pile, since, until));
}

async function listLeadsBare(
  pile: Pile, since?: string | null, until?: string | null,
): Promise<Lead[]> {
  const sb = await db();
  const cut = agedBefore();
  const win = (q: any) => {
    const w = since ? q.gte("created_at", since) : q;
    return until ? w.lte("created_at", until) : w;
  };

  // NEWEST FIRST, everywhere. Anthony: "Leads column is sorted oldest to
  // newest that contacted us, reverse this please."
  //
  // The old order put the coldest lead on top on the theory that it was
  // the most urgent. In a real morning it is the opposite: somebody who
  // rang twenty minutes ago is still holding their phone, and a lead from
  // nine days ago has already booked somewhere else. Answering the fresh
  // one is worth more than chasing the cold one.
  //
  // "check if they've been contacted though" — the risk of this order is
  // that an old lead nobody rang sinks out of sight. So it is not left to
  // the sort: `neverContacted` is on every row and the screen counts them
  // at the top, which surfaces the problem without giving up the order.
  /**
   * NOTHING FROM THE MIGRATION IN THE PILES THAT GET WORKED.
   *
   * Waiting and Booked are lists somebody acts on this morning. An
   * imported lead in there is a person who rang months ago, whose row says
   * they rang in August because that is when the database was loaded.
   *
   * Lost is left alone: it is a record rather than a queue, and hiding
   * history from the pile whose whole job is to hold history would be the
   * one place this floor does harm.
   */
  const base = () => win(sb.from("enquiries").select(SEL).eq("kind", "lead")
    .gte("created_at", IMPORT_WATERMARK).limit(300));
  const anyTime = () => win(sb.from("enquiries").select(SEL).eq("kind", "lead").limit(300));

  if (pile === "waiting") {
    // `converted_job_id` is NOT excluded here any more: a lead holding an
    // unanswered quote is still waiting, and pileOf decides which of the
    // rows that come back really belong on this screen.
    const { data, error } = await base()
      .in("status", ["new", "contacted"]).gte("created_at", cut)
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);

    // Sorted by when the thread last moved rather than when it arrived,
    // so the desk sees what is live. This is the pile that is worked;
    // booked and lost are records and stay in arrival order.
    const rows = oneCardPerPerson(
      ((data ?? []) as unknown as Lead[]).filter((l) => pileOf(l) === "waiting"));
    const [seen, flags] = await Promise.all([lastActivity(rows), openFlags(rows)]);
    for (const l of rows) {
      l.moved_at = movedAt(l, seen);
      l.call_now = needsCalling(l, seen);
      l.last_move = lastMove(l, seen);

      const p10 = String(l.customer_phone ?? "").replace(/\D/g, "").slice(-10);
      const t = seen.get(p10);
      l.stage = stageOf({
        textsOut: t?.tried ?? 0,
        lastIn: t?.lastIn ? Date.parse(t.lastIn) : null,
        lastOut: t?.lastOut ? Date.parse(t.lastOut) : null,
        called: Boolean(t?.called),
        quoted: Boolean(l.converted_job_id),
      });
      l.last_said = t?.lastBody
        ? { body: t.lastBody, who: t.lastWho ?? "us" } : null;

      const f = flags.get(p10);
      l.flag = f?.level ?? null;
      l.flag_why = f?.title ?? null;
      l.flag_kind = f?.kind ?? null;
    }
    return rows.sort((a, b) => (b.moved_at ?? "").localeCompare(a.moved_at ?? ""));
  }

  if (pile === "booked") {
    // Marked booked by hand, OR carrying a quote somebody said yes to.
    // The second is the common one now and was previously the ONLY one,
    // for every quote whether answered or not.
    const [marked, quoted] = await Promise.all([
      base().in("status", ["booked", "converted"]).order("created_at", { ascending: false }),
      base().in("status", ["new", "contacted"]).not("converted_job_id", "is", null)
        .order("created_at", { ascending: false }),
    ]);
    if (marked.error) throw new Error(marked.error.message);
    if (quoted.error) throw new Error(quoted.error.message);
    const seen = new Set<string>();
    return oneCardPerPerson(
      [...((marked.data ?? []) as unknown as Lead[]),
       ...((quoted.data ?? []) as unknown as Lead[]).filter(quoteApproved)]
        .filter((l) => (seen.has(l.id) ? false : seen.add(l.id)))
        .sort((a, b) => b.created_at.localeCompare(a.created_at)));
  }

  // Lost is two groups: closed by somebody, and aged out on its own. One
  // query each rather than a clever `or`, because the two are different
  // things and the screen says which is which.
  const [closed, aged] = await Promise.all([
    anyTime().in("status", ["lost", "cancelled"]).order("created_at", { ascending: false }),
    anyTime().in("status", ["new", "contacted"]).lt("created_at", cut)
      .order("created_at", { ascending: false }),
  ]);
  if (closed.error) throw new Error(closed.error.message);
  return oneCardPerPerson(
    [...((closed.data ?? []) as unknown as Lead[]), ...((aged.data ?? []) as unknown as Lead[])]
      .sort((a, b) => b.created_at.localeCompare(a.created_at)));
}

/**
 * THE TABS COUNT THE SAME WAY THE LISTS SORT.
 *
 * These were four head-counts over `status`, which is not what decides a
 * pile: a lead carrying an unanswered quote counted as Waiting on the tab
 * and appeared under Booked in the list, so the number above a screen
 * disagreed with the rows on it. A count you cannot click through to is a
 * number nobody believes twice.
 *
 * So every lead that could be in any pile is classified by `pileOf`, the
 * one function the lists use. It reads a few hundred narrow rows rather
 * than four counts, which is worth it to make the two agree by
 * construction rather than by both being careful.
 */
export async function countPiles(): Promise<Record<Pile, number>> {
  const sb = await db();
  // Counted the same way the lists are built, or the tab disagrees with
  // the screen it opens. Lost carries the migration; the other two do not.
  const { data, error } = await sb.from("enquiries")
    .select("id,status,created_at,converted_job_id,customer_phone," +
            "jobs:converted_job_id(status,quote_expires_at,changes_requested_at," +
            "job_authorizations(id))")
    .eq("kind", "lead")
    .order("created_at", { ascending: false })
    .limit(1000);
  if (error) throw new Error(error.message);

  const out: Record<Pile, number> = { waiting: 0, booked: 0, lost: 0 };
  // COUNTED AFTER THE COLLAPSE, or the tab says forty and opens a screen
  // holding twenty-six. The lists show one card per person now, so the
  // number above them has to be people too.
  for (const l of oneCardPerPerson((data ?? []) as unknown as Lead[])) {
    const pile = pileOf(l);
    // Waiting and Booked are floored at the watermark in listLeads, so the
    // counts have to be floored the same way or a tab says forty and opens
    // a screen holding six.
    if (pile !== "lost" && l.created_at < IMPORT_WATERMARK) continue;
    out[pile] += 1;
  }
  return out;
}

/**
 * CLEAR THE RED BY HAND.
 *
 * Anthony: "make sure that we have an option to clear leads marked red if
 * the system doesn't for some reason."
 *
 * Red comes off on its own when somebody rings them or they write back,
 * and that covers nearly every case. Nearly is the problem. A technician
 * who rang from a personal phone, a call the tracking never logged, a
 * customer dealt with in the doorway -- none of those reach the ledger, so
 * the tag stays red on somebody who has been sorted. A flag that cannot be
 * cleared by the person who knows it is finished is a flag that gets
 * ignored, which is exactly how the old one stopped meaning anything.
 *
 * It records WHO said so, because that is the difference between clearing
 * a flag and hiding one.
 */
export async function clearFlagFor(phone10: string): Promise<number> {
  const p10 = String(phone10 ?? "").replace(/\D/g, "").slice(-10);
  if (p10.length !== 10) return 0;
  const sb = await db();
  const { data: me } = await sb.auth.getUser();
  const { data, error } = await sb.from("notifications")
    .update({
      cleared_at: new Date().toISOString(),
      cleared_by: me.user?.id ?? null,
      cleared_how: "cleared by hand from the lead",
    })
    .eq("phone10", p10).is("cleared_at", null).select("id");
  if (error) throw new Error(error.message);
  return (data ?? []).length;
}

/** Move one lead along the funnel by hand. */
/**
 * THE WORD THE FILTER USES, so a hand-marked one and a caught one are the
 * same thing to anybody counting. The arrival filter writes "Filed as spam
 * on arrival: ...", this writes "Marked as spam by the desk", and
 * `lost_reason ilike '%spam%'` finds both.
 */
export const SPAM_REASON = "Marked as spam by the desk.";

export async function setLeadStatus(
  id: string, status: Lead["status"], reason?: string,
): Promise<void> {
  const sb = await db();
  const patch: Record<string, unknown> = { status };
  if (status === "lost") patch["lost_reason"] = (reason ?? "").trim() || "Closed by the desk.";
  const { error } = await sb.from("enquiries").update(patch).eq("id", id);
  if (error) throw new Error(error.message);
}

/**
 * Write the week rule into the record for everything it already applies to.
 *
 * Offered as a button rather than run on a timer. Closing somebody's
 * enquiry is a decision, and the person doing it should be able to see how
 * many they are closing first.
 */
export async function closeAgedOut(): Promise<number> {
  const sb = await db();
  const { data, error } = await sb.from("enquiries")
    .update({ status: "lost", lost_reason: `No answer within ${AGED_DAYS} days and no quote raised.` })
    .eq("kind", "lead").in("status", ["new", "contacted"]).lt("created_at", agedBefore())
    .select("id");
  if (error) throw new Error(error.message);
  return (data ?? []).length;
}

/**
 * TURN A LEAD INTO A QUOTE — the advisor's whole morning, in one call.
 *
 * This did not exist. Every screen was built to READ work that had already
 * been imported, so nothing ever noticed that the application could not
 * create a job at all. Walking the advisor's day found it in a minute:
 * step two of their track was a button nobody had written.
 *
 * What it does, in order, because the order matters:
 *
 *   1. Makes sure there is a CUSTOMER. A lead carries a loose name and
 *      phone; a job needs a real record. If the lead already points at one
 *      we use it, and if it does not we match on the phone before creating
 *      anything — the same person ringing twice is one customer, not two.
 *   2. Makes sure there is a CAR, when we know enough to identify one.
 *   3. Creates the job as an ESTIMATE. Never further along than that: it is
 *      not booked, nobody has agreed a price, and no work may start.
 *   4. Marks the lead converted and points it at the job, so the funnel
 *      stops counting it as somebody still waiting for a call back.
 *
 * The job number comes from the database, not from here.
 */
export async function convertToQuote(
  lead: Lead,
  car?: { year?: number | null; make?: string; model?: string; plate?: string;
          vin?: string; colour?: string },
): Promise<string> {
  const sb = await db();

  const { data: me } = await sb.auth.getUser();
  const { data: prof } = await sb.from("profiles")
    .select("account_id").eq("id", me.user?.id ?? "").maybeSingle();
  const accountId = (prof as { account_id: string } | null)?.account_id;
  if (!accountId) throw new Error("Cannot tell which shop you belong to. Sign in again.");

  /**
   * ── 0. HAS THIS LEAD ALREADY BECOME A QUOTE ────────────────────────
   *
   * Anthony: "there is an issue with duplicate quotes".
   *
   * Here is how they were made. Step 4 below marks the lead converted and
   * is ALLOWED TO FAIL — it only logs — on the reasoning that a quote with
   * a lead still showing as waiting is the harmless direction to fail in.
   * It is not harmless. A lead still sitting in the pile is a lead the
   * next advisor converts, and now the customer has two quotes with two
   * numbers for one car. Two people working the board at once does it
   * without any failure at all, and so does a double tap on a slow phone.
   *
   * So conversion is idempotent: ask first, and hand back the job that
   * already exists. Reading `enquiry_id` off jobs rather than trusting
   * `converted_job_id` on the lead is deliberate — the whole problem is
   * that the write to the lead is the one that goes missing, so the job
   * is the more reliable witness of the two.
   */
  const { data: already } = await sb.from("jobs")
    .select("id,status").eq("enquiry_id", lead.id)
    .neq("status", "cancelled").limit(1).maybeSingle();
  if (already) {
    const j = already as { id: string };
    // Repair the link that probably went missing, then hand back the
    // quote we already have instead of raising a second one.
    await sb.from("enquiries")
      .update({ status: "converted", converted_job_id: j.id }).eq("id", lead.id);
    return j.id;
  }

  // ── 1. the customer ────────────────────────────────────────────────
  let customerId = lead.customer_id;
  const phone10 = String(lead.customer_phone ?? "").replace(/\D/g, "").slice(-10);

  if (!customerId && phone10.length === 10) {
    const { data: hit } = await sb.from("customers")
      .select("id").eq("phone10", phone10).limit(1).maybeSingle();
    customerId = (hit as { id: string } | null)?.id ?? null;
  }

  if (!customerId) {
    const name = (lead.customer_name ?? "").trim();
    if (!name) throw new Error("This lead has no name on it. Add one before raising a quote.");
    const { data, error } = await sb.from("customers").insert({
      account_id: accountId, full_name: name,
      phone: lead.customer_phone ?? null, email: lead.customer_email ?? null,
    }).select("id").single();
    if (error) throw new Error(`Could not create the customer: ${error.message}`);
    customerId = (data as { id: string }).id;
  }

  // ── 2. the car ─────────────────────────────────────────────────────
  // Only if we can actually identify one. A blank vehicle row helps nobody
  // and the database refuses it anyway.
  let vehicleId = lead.vehicle_id;
  const named = Boolean(car?.make || car?.plate || car?.vin);
  if (!vehicleId && named) {
    // `license_plate`, not `plate`. It was `plate` here, which is not a
    // column, so PostgREST answered 400 and EVERY conversion that named a
    // car failed at this line — the customer and the quote never got made
    // and the advisor saw "Could not save the vehicle" with no idea why.
    // Blank strings go in as null; the box being empty is not a value.
    const nn = (v: string | null | undefined) => (v ?? "").trim() || null;
    const { data, error } = await sb.from("vehicles").insert({
      account_id: accountId, customer_id: customerId,
      year: car?.year ?? null, make: nn(car?.make), model: nn(car?.model),
      license_plate: nn(car?.plate), vin: nn(car?.vin),
      /* Collected on the lead sheet for trades that use it. The column
         has existed since 0001 and nothing ever filled it in. */
      colour: nn(car?.colour),
    }).select("id").single();
    if (error) throw new Error(carSaveRefusal("Could not save the vehicle", error));
    vehicleId = (data as { id: string }).id;
  }

  // ── 3. the job ─────────────────────────────────────────────────────
  const { data: job, error: jobErr } = await sb.from("jobs").insert({
    account_id: accountId, customer_id: customerId, vehicle_id: vehicleId,
    enquiry_id: lead.id, status: "estimate",
    title: (lead.service_wanted ?? "").trim() || "New quote",
    complaint: lead.notes ?? null,
    created_by: me.user?.id ?? null,
  }).select("id,job_number").single();
  if (jobErr) throw new Error(`Could not raise the quote: ${jobErr.message}`);
  const j = job as { id: string; job_number: string };

  // ── 4. close the loop on the lead ──────────────────────────────────
  // Last, deliberately. If this fails the quote still exists and the lead
  // is merely still showing as waiting, which is the harmless direction to
  // fail in — the opposite would lose the work.
  const { error: leadErr } = await sb.from("enquiries")
    .update({ status: "converted", converted_job_id: j.id }).eq("id", lead.id);
  if (leadErr) console.error("lead not marked converted:", leadErr.message);

  /**
   * THE NOTES COME WITH IT.
   *
   * Everything an advisor learned on the phone was written on the lead,
   * and the moment it becomes a quote nobody opens the lead again. So the
   * notes are stamped with the job as well — they keep their enquiry_id,
   * so they still read on the lead, and they now read on the job where
   * the work is actually done.
   *
   * A failure here is logged and not thrown, for the same reason as the
   * line above: the quote exists, and losing it over a note that stayed
   * on the lead would be the wrong trade.
   */
  const { error: noteErr } = await sb.from("notes")
    .update({ job_id: j.id }).eq("enquiry_id", lead.id).is("job_id", null);
  if (noteErr) console.error("lead notes did not follow onto the job:", noteErr.message);

  return j.id;
}
